import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import clone from 'lodash.clonedeep';
import firebase from 'firebase/compat/app';
import { setCurrencyInfo } from '@cardladder/shared/utils/format.util';
import { authService, indexService } from '@/services';

import {
  DEFAULT_CARD_FILTERS,
  DEFAULT_COLLECTION_FILTERS,
  DEFAULT_SHOP_FILTERS,
  DEFAULT_ADD_CARD_FILTERS,
  DEFAULT_PENDING_FILTERS,
  DEFAULT_PROFILE_SEARCH_FILTERS,
  DEFAULT_SHOWCASE_FILTERS,
  DEFAULT_PLAYER_INDEX_FILTERS,
} from '@cardladder/shared/constants';

Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      key: 'cardladder',
      paths: [
        'ladderSort',
        'ladderDirection',
        'ladderFilterMap',
        'ladderQuery',
        'collectionSort',
        'collectionFilters',
        'collectionDirection',
        'collectionQuery',
        'watchlistSort',
        'watchlistFilters',
        'watchlistDirection',
        'indexSort',
        'indexDirection',
        'indexQuery',
        'playerIndexFilterMap',
        'playerIndexQuery',
        'feedFilter',
        'cardGraphZoom',
        'cardGraphScale',
        'compareGraphZoom',
        'compareGraphScale',
        'compareType',
        'pendingSalesQuery',
        'pendingSalesSort',
        'pendingSalesDirection',
        'pendingSalesFilters',
        'collectionViewType',
        'shopViewType',
        'salesViewType',
        'navCollapsed',
        'activeCollectionId',
        'listingsSort',
        'listingsDirection',
        'listingsFilterMap',
        'listingsQuery',
        'addCardQuery',
        'addCardFilterMap',
        'addCardSort',
        'addCardDirection',
        'showcaseSort',
        'showcaseDirection',
        'showcaseFilterMap',
        'showcaseQuery',
        'showcaseCollectionsQuery',
        'showcaseDisplay',
        'auditLogQuery',
        'profileSearchQuery',
        'profileSearchFilterMap',
        'globalSearchSource',
        'salesHistoryQuery',
        'salesHistoryViewType',
      ],
    }),
  ],
  state: {
    navCollapsed: false,
    navOpen: false,
    notificationsOpen: false,
    ladderSort: 'score',
    ladderDirection: 'desc',
    ladderFilterMap: clone(DEFAULT_CARD_FILTERS),
    ladderQuery: '',
    collectionSort: 'dateAdded',
    collectionDirection: 'desc',
    collectionFilters: clone(DEFAULT_COLLECTION_FILTERS),
    collectionQuery: '',
    collectionSelectedIds: [],
    watchlistSort: 'currentValue',
    watchlistDirection: 'desc',
    watchlistFilters: clone(DEFAULT_CARD_FILTERS),
    indexDisplay: 'total',
    indexSort: 'dailySales',
    indexDirection: 'desc',
    indexQuery: '',
    playerIndexFilterMap: clone(DEFAULT_PLAYER_INDEX_FILTERS),
    playerIndexQuery: '',
    feedFilter: 'all',
    pendingSalesQuery: '',
    pendingSalesSort: 'platform',
    pendingSalesDirection: 'desc',
    pendingSalesFilters: clone(DEFAULT_PENDING_FILTERS),
    pendingSalesEditing: false,
    cardGraphZoom: 92,
    cardGraphScale: 'linear',
    compareGraphZoom: 92,
    compareGraphScale: 'linear',
    collectionViewType: 'cards',
    shopViewType: 'grid',
    salesViewType: 'list',
    compareType: 'cards',
    compareCards: [],
    compareIndexes: [],
    user: {},
    collection: {},
    activeCollectionId: null,
    userListener: null,
    collectionListener: null,
    listingsSort: 'createdDate',
    listingsDirection: 'asc',
    listingsFilterMap: clone(DEFAULT_SHOP_FILTERS),
    listingsQuery: '',
    listingsFeedView: false,
    listingsFeed: [],
    addCardQuery: '',
    addCardFilterMap: clone(DEFAULT_ADD_CARD_FILTERS),
    addCardSort: 'dateAdded',
    addCardDirection: 'desc',
    showcaseSort: 'dateAdded',
    showcaseDirection: 'desc',
    showcaseFilterMap: clone(DEFAULT_SHOWCASE_FILTERS),
    showcaseQuery: '',
    showcaseCollectionsQuery: '',
    showcaseDisplay: 'cards',
    reportsQuery: '',
    indexesSort: 'monthlyPercentChange',
    indexesDirection: 'desc',
    indexSnapshot: {},
    employeeOptions: [],
    globalSearchSource: 'salesarchive',
    profileSearchQuery: '',
    profileSearchFilterMap: clone(DEFAULT_PROFILE_SEARCH_FILTERS),
    salesHistoryQuery: 'direction=desc&sort=date',
    salesHistoryViewType: 'list',
  },

  mutations: {
    SET_NAV_COLLAPSED(state, value) {
      state.navCollapsed = value;
    },

    SET_NAV_OPEN(state, open) {
      state.navOpen = open;
    },

    SET_NOTIFICATIONS_OPEN(state, open) {
      state.notificationsOpen = open;
    },

    SET_LADDER_SORT(state, sort) {
      state.ladderSort = sort;
    },

    SET_LADDER_DIRECTION(state, direction) {
      state.ladderDirection = direction;
    },

    SET_LADDER_FILTERS(state, filters) {
      state.ladderFilterMap = filters;
    },

    SET_LADDER_QUERY(state, query) {
      state.ladderQuery = query;
    },

    SET_COLLECTION_SORT(state, sort) {
      state.collectionSort = sort;
    },

    SET_COLLECTION_DIRECTION(state, direction) {
      state.collectionDirection = direction;
    },

    SET_COLLECTION_SELECTED_IDS(state, selectedIds) {
      state.collectionSelectedIds = selectedIds;
    },

    SET_COLLECTION_FILTERS(state, filters) {
      state.collectionFilters = filters;
    },

    SET_COLLECTION_QUERY(state, query) {
      state.collectionQuery = query;
    },

    SET_WATCHLIST_SORT(state, sort) {
      state.watchlistSort = sort;
    },

    SET_WATCHLIST_DIRECTION(state, direction) {
      state.watchlistDirection = direction;
    },

    SET_WATCHLIST_FILTERS(state, filters) {
      state.watchlistFilters = filters;
    },

    SET_COMPARE_TYPE(state, type) {
      state.compareType = type;
    },

    SET_COMPARE_CARDS(state, cards) {
      state.compareCards = cards;
    },

    SET_COMPARE_INDEXES(state, indexes) {
      state.compareIndexes = indexes;
    },

    SET_INDEX_SORT(state, sort) {
      state.indexSort = sort;
    },

    SET_INDEX_DIRECTION(state, direction) {
      state.indexDirection = direction;
    },

    SET_INDEX_QUERY(state, query) {
      state.indexQuery = query;
    },

    SET_PLAYER_INDEX_FILTERS(state, filters) {
      state.playerIndexFilterMap = filters;
    },

    SET_PLAYER_INDEX_QUERY(state, query) {
      state.playerIndexQuery = query;
    },

    SET_FEED_FILTER(state, filter) {
      state.feedFilter = filter;
    },

    SET_CARD_GRAPH_ZOOM(state, zoom) {
      state.cardGraphZoom = zoom;
    },

    SET_CARD_GRAPH_SCALE(state, scale) {
      state.cardGraphScale = scale;
    },

    SET_COMPARE_GRAPH_ZOOM(state, zoom) {
      state.compareGraphZoom = zoom;
    },

    SET_COMPARE_GRAPH_SCALE(state, scale) {
      state.compareGraphScale = scale;
    },

    SET_COLLECTION_VIEW_TYPE(state, type) {
      state.collectionViewType = type;
    },

    SET_SHOP_VIEW_TYPE(state, type) {
      state.shopViewType = type;
    },

    SET_SALES_VIEW_TYPE(state, type) {
      state.salesViewType = type;
    },

    SET_USER(state, user) {
      state.user = user;
    },

    UPDATE_USER(state, user) {
      state.user = { ...state.user, ...user };
    },

    UPDATE_USER_CURRENCY(state, currencyMap) {
      if (state.user?.locale && currencyMap && currencyMap[state.user.locale]) {
        state.user.currencyInfo = currencyMap[state.user.locale];
        setCurrencyInfo(state.user.currencyInfo);
      }
    },

    SET_USER_LISTENER(state, listener) {
      state.userListener = listener;
    },

    SET_ACTIVE_COLLECTION_ID(state, activeCollectionId) {
      state.activeCollectionId = activeCollectionId;
    },

    SET_COLLECTION(state, collection) {
      state.collection = collection;
    },

    SET_COLLECTIONS(state, collections) {
      Vue.set(state, 'collections', [...collections]);
    },

    STOP_USER_LISTENER(state) {
      if (typeof state.userListener === 'function') {
        state.userListener();
      }

      state.userListener = null;
    },

    SET_COLLECTION_LISTENER(state, listener) {
      state.collectionListener = listener;
    },

    STOP_COLLECTION_LISTENER(state) {
      if (typeof state.collectionListener === 'function') {
        state.collectionListener();
      }

      state.collectionListener = null;
    },

    SET_PENDING_QUERY(state, query) {
      state.pendingSalesQuery = query;
    },

    SET_PENDING_SORT(state, sort) {
      state.pendingSalesSort = sort;
    },

    SET_PENDING_DIRECTION(state, direction) {
      state.pendingSalesDirection = direction;
    },

    SET_PENDING_FILTERS(state, filters) {
      state.pendingSalesFilters = filters;
    },

    SET_PENDING_SALES_EDITING(state, isEditing) {
      state.pendingSalesEditing = isEditing;
    },

    SET_LISTINGS_SORT(state, sort) {
      state.listingsSort = sort;
    },

    SET_LISTINGS_DIRECTION(state, direction) {
      state.listingsDirection = direction;
    },

    SET_LISTINGS_FILTERS(state, filters) {
      state.listingsFilterMap = filters;
    },

    SET_LISTINGS_QUERY(state, query) {
      state.listingsQuery = query;
    },

    SET_LISTINGS_FEED_VIEW(state, isListingsFeedView) {
      state.listingsFeedView = isListingsFeedView;
    },

    SET_LISTINGS_FEED(state, feed) {
      state.listingsFeed = feed;
    },

    SET_ADD_CARD_QUERY(state, query) {
      state.addCardQuery = query;
    },

    SET_ADD_CARD_FILTERS(state, filters) {
      state.addCardFilterMap = filters;
    },

    SET_ADD_CARD_SORT(state, sort) {
      state.addCardSort = sort;
    },

    SET_ADD_CARD_DIRECTION(state, direction) {
      state.addCardDirection = direction;
    },

    SET_SHOWCASE_SORT(state, sort) {
      state.showcaseSort = sort;
    },

    SET_SHOWCASE_DIRECTION(state, direction) {
      state.showcaseDirection = direction;
    },

    SET_SHOWCASE_FILTERS(state, filters) {
      state.showcaseFilterMap = filters;
    },

    SET_SHOWCASE_QUERY(state, query) {
      state.showcaseQuery = query;
    },

    SET_SHOWCASE_DISPLAY(state, display) {
      state.showcaseDisplay = display;
    },

    SET_SHOWCASE_COLLECTIONS_QUERY(state, query) {
      state.showcaseCollectionsQuery = query;
    },

    SET_INDEXES_SORT(state, sort) {
      state.indexesSort = sort;
    },

    SET_INDEX_SNAPSHOT(state, snapshot) {
      state.indexSnapshot = snapshot;
    },

    SET_INDEXES_DIRECTION(state, direction) {
      state.indexesDirection = direction;
    },

    SET_AUDIT_LOG_QUERY(state, query) {
      state.auditLogQuery = query;
    },

    SET_PROFILE_SEARCH_QUERY(state, query) {
      state.profileSearchQuery = query;
    },

    SET_PROFILE_SEARCH_FILTERS(state, filters) {
      state.profileSearchFilterMap = filters;
    },

    SET_EMPLOYEE_OPTIONS(state, employeeOptions) {
      state.employeeOptions = employeeOptions;
    },

    SET_GLOBAL_SEARCH_SOURCE(state, globalSearchSource) {
      state.globalSearchSource = globalSearchSource;
    },

    SET_SALES_HISTORY_QUERY(state, salesHistoryQuery) {
      state.salesHistoryQuery = salesHistoryQuery;
    },

    SET_SALES_HISTORY_VIEW_TYPE(state, salesHistoryViewType) {
      state.salesHistoryViewType = salesHistoryViewType;
    },
  },

  getters: {
    isAdmin: (state) => Boolean(state.user && state.user.role === 'admin'),
    isStaff: (state) => Boolean(state.user && (state.user.role === 'admin' || state.user.role === 'staff')),
    isMember: (state) => Boolean(state.user && (state.user.role === 'admin' || state.user.role === 'staff' || state.user.role === 'member')),
    isEmployee: (state) => Boolean(state.user && state.user.employee),
    currencyRatio: (state) => state.user && state.user.currencyInfo && state.user.currencyInfo.ratio ? state.user.currencyInfo.ratio : 1,
    currencySymbol: (state) => state.user && state.user.currencyInfo && state.user.currencyInfo.symbol ? state.user.currencyInfo.symbol : '$',
    darkMode: (state) => Boolean(state.user && state.user.theme === 'dark'),
  },

  actions: {
    async boot({ state, commit }, firebaseUser) {
      const theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      const user = await authService.loadUser(firebaseUser, theme);
      const { collections, collection } = await authService.loadCollections(user, state.activeCollectionId);
      const indexSnapshot = await indexService.getIndexSnapshot();

      commit('SET_USER', user);
      commit('SET_COLLECTIONS', collections);
      commit('SET_COLLECTION', collection);
      commit('SET_ACTIVE_COLLECTION_ID', collection.id);
      commit('SET_INDEX_SNAPSHOT', indexSnapshot);

      if (user && user.uid) {
        if (user.role === 'admin' || user.role === 'staff') {
          const employeeOptions = await authService.getEmployeeOptions();

          commit('SET_EMPLOYEE_OPTIONS', employeeOptions);
        }

        const currencyMap = await authService.getCurrencyMap();

        commit('UPDATE_USER_CURRENCY', currencyMap);

        if (!state.userListener) {
          const listener = firebase.firestore()
            .collection('users')
            .doc(user.uid)
            .onSnapshot((userDoc) => {
              commit('UPDATE_USER', userDoc.data());
            });

          commit('SET_USER_LISTENER', listener);
        }

        if (collections && collections.length && !state.collectionListener) {
          const listener = firebase.firestore()
            .collection('users')
            .doc(user.uid)
            .collection('collections')
            .onSnapshot((querySnapshot) => {
              const updatedCollections = [];

              querySnapshot.forEach((doc) => {
                updatedCollections.push({
                  id: doc.id,
                  numCards: 0,
                  totalNumCards: 0,
                  totalValue: 0,
                  totalInvestment: 0,
                  totalNumSoldCards: 0,
                  totalSoldValue: 0,
                  totalSoldInvestment: 0,
                  ...doc.data(),
                });
              });

              commit('SET_COLLECTIONS', updatedCollections);

              if (Boolean(state.activeCollectionId) && updatedCollections.find(({ id }) => id === state.activeCollectionId)) {
                commit('SET_COLLECTION', updatedCollections.find(({ id }) => id === state.activeCollectionId));
              } else {
                commit('SET_COLLECTION', {});
              }
            });

          commit('SET_COLLECTION_LISTENER', listener);
        }
      }

      return user;
    },

    async logout({ commit }) {
      commit('SET_USER', {});
      commit('SET_COLLECTION', {});
      commit('SET_COLLECTIONS', []);
      commit('SET_ACTIVE_COLLECTION_ID', null);
      commit('STOP_USER_LISTENER');
      commit('STOP_COLLECTION_LISTENER');
    },
  },
});
